'use client';

// Canonical source for all three worker apps. scripts/sync-house-ads.mjs
// generates the tools/blog copies; edit this root file only.

import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Detects that AdSense declined to fill a slot, so a house ad can claim the
 * otherwise-worthless inventory.
 *
 * DELIBERATELY CONSERVATIVE. A false positive replaces a *paying* impression,
 * which costs real money; a false negative just leaves a slot empty, exactly as
 * today. So every ambiguous state resolves to "filled" and we do nothing.
 *
 * The three states observed in production (2026-07-27, live DOM inspection):
 *
 *   data-ad-status="filled"    + iframe  -> a real ad. Never touch.
 *   data-ad-status="unfilled"  + iframe  -> requested, no ad returned. The
 *                                           iframe is a 0-height placeholder,
 *                                           so "has an iframe" alone is NOT a
 *                                           safe fill test.
 *   status="done", no data-ad-status, no iframe -> the common case on this
 *                                           site's responsive units. Nothing
 *                                           rendered; safe to claim.
 *
 * Timing: AdSense sets these attributes asynchronously after the auction
 * resolves. DEFAULT_DELAY_MS is generous on purpose — waiting too long only
 * delays a house ad, whereas checking too early would misread a slow fill as
 * unfilled and destroy revenue.
 */
const DEFAULT_DELAY_MS = 3000;

export interface AdFallbackState {
  /** The slot is ours to claim — the network did not fill it. */
  unfilled: boolean;
  /**
   * The ad library never loaded, i.e. a blocker stopped it. Distinct from
   * unfilled: both look identical in the DOM, but a blocked visitor also cannot
   * load network banner artwork and quite likely cannot reach an affiliate
   * redirector, so only our own products work end to end for them.
   */
  blocked: boolean;
}

/**
 * Which surface this page is on, from the public URL.
 *
 * Derived at runtime rather than passed in so the generated file behaves the
 * same in all three worker apps — and because each app's router reports a
 * different internal path anyway (apps/tools serves /tools/x from /[category],
 * apps/blog serves /blog/x from /[slug]).
 */
function surfaceFromPath(): 'main' | 'tools' | 'blog' {
  if (typeof window === 'undefined') return 'main';
  const p = window.location.pathname;
  if (p.startsWith('/tools')) return 'tools';
  if (p.startsWith('/blog')) return 'blog';
  return 'main';
}

/**
 * Report one slot's outcome so fill rate can be measured per placement.
 *
 * Fire-and-forget with keepalive, and failures are swallowed: this is
 * measurement, and it must never affect what the visitor sees. Note it posts
 * once per slot per pageview — if the write volume ever matters, sample here
 * rather than dropping placements, since a partial placement list would bias
 * exactly the comparison this exists to make.
 */
function reportOutcome(
  outcome: 'filled' | 'unfilled' | 'blocked',
  meta: { slot?: string; placement?: string },
): void {
  try {
    fetch('/api/session-signal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'slot',
        outcome,
        slot: meta.slot,
        placement: meta.placement,
        surface: surfaceFromPath(),
        path: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw into the page over measurement.
  }
}

export function useAdFallback(
  ref: RefObject<HTMLElement | null>,
  {
    delayMs = DEFAULT_DELAY_MS,
    slot,
    placement,
  }: { delayMs?: number; slot?: string; placement?: string } = {},
): AdFallbackState {
  const [unfilled, setUnfilled] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const settled = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (settled.current) return;
      settled.current = true;

      // adsbygoogle.js sets `loaded` once it initialises. Still missing after
      // the delay means the script never arrived — a blocker, not a no-bid.
      const ads = (window as unknown as { adsbygoogle?: { loaded?: boolean } })
        .adsbygoogle;
      if (!ads?.loaded) setBlocked(true);

      const el = ref.current;
      if (!el) return;

      const status = el.getAttribute('data-ad-status');

      const blockedNow = !ads?.loaded;

      // Explicit signals win.
      if (status === 'filled') {
        reportOutcome('filled', { slot, placement });
        return;
      }
      if (status === 'unfilled') {
        reportOutcome(blockedNow ? 'blocked' : 'unfilled', { slot, placement });
        setUnfilled(true);
        return;
      }

      // No explicit status: fall back to whether anything actually rendered.
      // Require a *visible* frame — AdSense leaves 0-height placeholders behind.
      const hasVisibleFrame = Array.from(el.querySelectorAll('iframe')).some(
        (f) => (f as HTMLIFrameElement).offsetHeight > 1,
      );
      if (!hasVisibleFrame) {
        reportOutcome(blockedNow ? 'blocked' : 'unfilled', { slot, placement });
        setUnfilled(true);
      } else {
        reportOutcome('filled', { slot, placement });
      }
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [ref, delayMs, slot, placement]);

  return { unfilled, blocked };
}
